"use client"

import React, { useState, useEffect } from "react"
import Link from "next/link"
import { usePathname } from "next/navigation"
import { X, Menu } from "lucide-react"
import {
	Sheet,
	SheetContent,
	SheetHeader,
	SheetTitle,
	SheetTrigger,
	SheetClose,
	SheetDescription,
	SheetOverlay,
	SheetPortal,
} from "@/components/animate-ui/primitives/radix/sheet"
import { cn } from "@/lib/utils"

interface NavItem {
	id: string
	label: string
	url: string | { slug: string } | null
	isCta?: boolean
}

interface MobileNavProps {
	navigation: NavItem[]
}

export const MobileNav: React.FC<MobileNavProps> = ({ navigation }) => {
	const pathname = usePathname()
	const [mounted, setMounted] = useState(false)

	useEffect(() => {
		setMounted(true)
	}, [])

	// Filtrar solo los elementos que no son CTA
	const navItems = navigation.filter((item) => !item.isCta)

	// Si no hay navegación, no mostrar nada
	if (navItems.length === 0) {
		return null
	}

	const getHref = (url: string | { slug: string } | null): string => {
		if (!url) return "#"
		if (typeof url === "string") return url
		const slug = url.slug
		if (slug === "home" || slug === "/") return "/"
		return `/${slug}`
	}

	return (
		<Sheet defaultOpen={false}>
			<SheetTrigger asChild>
				<button
					type="button"
					className={cn(
						"inline-flex items-center justify-center rounded-lg p-2",
						"text-gray-600 transition-colors hover:bg-gray-100 hover:text-primary",
						"focus:outline-none focus:ring-2 focus:ring-primary/50",
						"lg:hidden", // Solo visible en mobile/tablet
					)}
					aria-label="Abrir menú de navegación"
				>
					<Menu className="h-6 w-6" />
				</button>
			</SheetTrigger>
			<SheetPortal>
				<SheetOverlay />
				<SheetContent
					side="right"
					className="w-full max-w-xs sm:max-w-sm z-[60] bg-white"
				>
					<SheetHeader className="flex items-center justify-between border-b border-gray-100 pb-4 px-4">
						<SheetTitle className="text-lg font-semibold text-gray-900">
							Menú
						</SheetTitle>
						<SheetDescription className="sr-only">
							Menú de navegación móvil con enlaces a las páginas del sitio
						</SheetDescription>
						<SheetClose asChild>
							<button
								type="button"
								className="rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-primary/50"
								aria-label="Cerrar menú"
							>
								<X className="h-5 w-5" />
							</button>
						</SheetClose>
					</SheetHeader>
					<nav className="mt-6 flex flex-col gap-2">
						{navItems.map((item) => {
							const href = getHref(item.url)
							const isActive =
								mounted &&
								(pathname === href ||
									(href !== "/" && pathname.startsWith(href)))

							return (
								<SheetClose asChild key={item.id || item.label}>
									<Link
										href={href}
										aria-current={isActive ? "page" : undefined}
										className={cn(
											"flex items-center rounded-lg px-4 py-3 text-base font-medium",
											"transition-colors hover:bg-gray-50 hover:text-primary",
											"focus:outline-none focus:ring-2 focus:ring-primary/50",
											isActive ? "bg-gray-50 text-primary" : "text-gray-600",
										)}
									>
										{item.label}
									</Link>
								</SheetClose>
							)
						})}
					</nav>
				</SheetContent>
			</SheetPortal>
		</Sheet>
	)
}
